/**
 * The confirmed outages of one site, newest first.
 *
 * Only confirmed ones: a single failed check that the next one contradicted
 * is a blip, and a list of blips would bury the outages a customer actually
 * noticed. The server already leaves them out; this list just draws what it
 * was given.
 *
 * An outage still open reads "ongoing" and its length keeps counting (the
 * server measures it to now). One the engine closed because the site stopped
 * being checked (paused, the client made inactive, the engine switched off;
 * `endedBy`) has an end the site did not choose: its length is the length of
 * what we saw, and it is drawn neutral with a line saying why it ended. A
 * green check there would read as a recovery nobody saw.
 */
import { CircleCheck, CircleSlash, CircleX } from "lucide-react";
import type { MonitorIncidentView } from "../../../lib/monitoring";
import { StatusPill, fmtDateTime } from "../../ui";
import { codeText, durationText, type T } from "./words";

/** The line for an outage the engine ended without seeing the site up. */
function endedKey(endedBy: string): string {
  if (endedBy === "paused") return "monitoring.incidents.ended.paused";
  if (endedBy === "client_inactive") return "monitoring.incidents.ended.clientInactive";
  if (endedBy === "engine_off") return "monitoring.incidents.ended.engineOff";
  return "monitoring.incidents.ended.stopped";
}

export function IncidentHistory({ incidents, t }: { incidents: MonitorIncidentView[]; t: T }) {
  if (incidents.length === 0) {
    return <p className="crm-mut px-[26px] pb-6 text-[13px]">{t("monitoring.incidents.empty")}</p>;
  }

  return (
    <ul className="flex flex-col px-[18px] pb-4">
      {incidents.map((inc) => {
        const open = inc.resolvedAt === null;
        const ended = !open && inc.endedBy !== null;
        const said = codeText(t, inc.code);
        return (
          <li key={inc.id} className="flex items-start gap-3 rounded-2xl px-2 py-2.5">
            {open ? (
              <CircleX className="mt-0.5 size-4 shrink-0 text-[var(--color-destructive)]" />
            ) : ended ? (
              <CircleSlash className="crm-mut mt-0.5 size-4 shrink-0" />
            ) : (
              <CircleCheck className="mt-0.5 size-4 shrink-0 text-[var(--color-success)]" />
            )}
            <span className="min-w-0 flex-1">
              <span className="crm-prow-n block">
                <span className="crm-tnum">{fmtDateTime(inc.startedAt)}</span>
                {" - "}
                {open ? t("monitoring.incidents.ongoing") : <span className="crm-tnum">{fmtDateTime(inc.resolvedAt)}</span>}
              </span>
              {said ? <span className="crm-prow-s block">{said}</span> : null}
              {ended ? <span className="crm-prow-s block">{t(endedKey(inc.endedBy!))}</span> : null}
            </span>
            <StatusPill tone={open ? "lt" : "mut"} className="shrink-0">
              {durationText(t, inc.durationSeconds)}
            </StatusPill>
          </li>
        );
      })}
    </ul>
  );
}
